import { useMemo } from 'react';

import { useSessionFilter } from './useSessionFilter';
import {
  DEFAULT_TOURISM_FILTER,
  buildTourismParams,
  computeTourismBadgeCounts,
  isTourismFilterActive,
  type TourismFilter,
} from '../types/tourismFilter';

export interface ActiveTourismChip {
  id: string;
  label: string;
  onRemove: () => void;
}

/**
 * Filterzustand der Seite „Daten".
 *
 * Bewusst ohne eigene API-Aufrufe: die Auswahllisten (Ortsteile, Anbieter,
 * Gruppen …) kommen gesammelt aus `/tourism/meta`, das die Seite ohnehin einmal
 * lädt. `useInserateFilter` holt dagegen beim Mount unbedingt die Vermieter —
 * hier wäre das ein Fremdkörper-Request.
 */
export function useTourismFilter(gruppeNames?: Map<number, string>) {
  const [filter, setFilterState] = useSessionFilter<TourismFilter>(
    'tourism_filter',
    DEFAULT_TOURISM_FILTER,
  );

  const setFilter = <K extends keyof TourismFilter>(key: K, value: TourismFilter[K]) =>
    setFilterState((prev) => ({ ...prev, [key]: value }));

  const resetFilter = () => setFilterState(DEFAULT_TOURISM_FILTER);

  const toggleIn = <K extends keyof TourismFilter>(key: K, value: string | number) =>
    setFilterState((prev) => {
      const current = prev[key] as unknown as (string | number)[];
      return {
        ...prev,
        [key]: current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value],
      };
    });

  const params = useMemo(() => buildTourismParams(filter), [filter]);
  const badgeCounts = useMemo(() => computeTourismBadgeCounts(filter), [filter]);
  const filterActive = useMemo(() => isTourismFilterActive(filter), [filter]);

  const activeChips = useMemo<ActiveTourismChip[]>(() => {
    const chips: ActiveTourismChip[] = [];

    const addMulti = (key: keyof TourismFilter, values: (string | number)[], label: (v: never) => string) => {
      values.forEach((value) =>
        chips.push({
          id: `${String(key)}:${value}`,
          label: label(value as never),
          onRemove: () => toggleIn(key, value),
        }),
      );
    };

    addMulti('listingTypes', filter.listingTypes, (v: string) => v);
    addMulti('neighborhoods', filter.neighborhoods, (v: string) => v);
    addMulti('platforms', filter.platforms, (v: string) => v === 'unknown' ? 'Unbekannt' : v);
    addMulti('zipcodes', filter.zipcodes, (v: string) => `PLZ ${v}`);
    addMulti(
      'gruppeIds',
      filter.gruppeIds,
      (v: number) => gruppeNames?.get(v) ?? `Gruppe ${v}`,
    );

    const addRange = (
      label: string,
      minKey: keyof TourismFilter,
      maxKey: keyof TourismFilter,
      unit = '',
    ) => {
      const min = filter[minKey] as string;
      const max = filter[maxKey] as string;
      if (!min && !max) return;
      const text = min && max ? `${min}–${max}` : min ? `ab ${min}` : `bis ${max}`;
      chips.push({
        id: `range:${String(minKey)}`,
        label: `${label} ${text}${unit}`,
        onRemove: () =>
          setFilterState((prev) => ({ ...prev, [minKey]: '', [maxKey]: '' })),
      });
    };

    addRange('Preis', 'priceMin', 'priceMax', ' €');
    addRange('Größe', 'sizeMin', 'sizeMax', ' m²');
    addRange('Gäste', 'guestsMin', 'guestsMax');
    addRange('Schlafzimmer', 'bedroomsMin', 'bedroomsMax');
    addRange('Bewertung', 'ratingMin', 'ratingMax');

    return chips;
    // `filter` und `gruppeNames` decken alle gelesenen Werte ab;
    // toggleIn/setFilterState sind stabil.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, gruppeNames]);

  return {
    filter,
    setFilter,
    setFilterState,
    resetFilter,
    toggleIn,
    params,
    badgeCounts,
    filterActive,
    activeChips,
  };
}
